import { ApiKeyScope } from '../types/apiKey';

export interface ApiKeyScopeOption {
  value: ApiKeyScope;
  label: string;
  description: string;
}

export const API_KEY_SCOPE_OPTIONS: ApiKeyScopeOption[] = [
  {
    value: 'read',
    label: 'Read',
    description: 'Read organization metadata, repositories, scans, findings, fixes, and quotas.',
  },
  {
    value: 'scan:write',
    label: 'Start scans',
    description: 'Create and trigger repository scans.',
  },
  {
    value: 'finding:write',
    label: 'Update findings',
    description: 'Create finding events and update finding state.',
  },
  {
    value: 'fix:write',
    label: 'Generate fixes',
    description: 'Create and run fix attempts.',
  },
  {
    value: 'pr:write',
    label: 'Open pull requests',
    description: 'Create pull requests from generated fixes.',
  },
];

export const ALL_API_KEY_SCOPES = API_KEY_SCOPE_OPTIONS.map((option) => option.value);

export interface ApiKeyScopeSummary {
  label: string;
  title: string;
}

const scopeLabelByValue = API_KEY_SCOPE_OPTIONS.reduce<Record<ApiKeyScope, string>>(
  (labels, option) => {
    labels[option.value] = option.label;
    return labels;
  },
  {} as Record<ApiKeyScope, string>
);

export const hasAllApiKeyScopes = (scopes?: ApiKeyScope[] | null): boolean => {
  if (!scopes || scopes.length === 0) {
    return false;
  }

  const uniqueScopes = new Set(scopes);
  return ALL_API_KEY_SCOPES.every((scope) => uniqueScopes.has(scope));
};

export const summarizeApiKeyScopes = (scopes?: ApiKeyScope[] | null): ApiKeyScopeSummary => {
  if (!scopes || scopes.length === 0) {
    return {
      label: 'Legacy full access',
      title: 'This key was created before scopes and can use every public API endpoint.',
    };
  }

  if (hasAllApiKeyScopes(scopes)) {
    return {
      label: 'Full access',
      title: API_KEY_SCOPE_OPTIONS.map((option) => option.label).join(', '),
    };
  }

  if (scopes.length === 1 && scopes[0] === 'read') {
    return {
      label: 'Read-only',
      title: scopeLabelByValue.read,
    };
  }

  return {
    label: 'Custom access',
    title: scopes.map((scope) => scopeLabelByValue[scope] || scope).join(', '),
  };
};
